import React, { useRef, useEffect, useState } from 'react';
import './App.css'; // Uses your polka dot background and font

function InstallPage() {
    const buttonRef = useRef(null);
    const [cursorPos, setCursorPos] = useState({ top: 200, left: 100 }); // Start position
    const [hasMoved, setHasMoved] = useState(false);
    
    // Platform toggle state (commented out since we're focusing on demo)
    // const [selectedPlatform, setSelectedPlatform] = useState('mac'); // Default to Mac
    
    // Mobile detection
    const [isMobile, setIsMobile] = useState(false);
    
    // Typing animation state
    const [currentTextIndex, setCurrentTextIndex] = useState(0);
    const [currentText, setCurrentText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [typingSpeed, setTypingSpeed] = useState(50);

    // Waitlist form state
    const [showWaitlistForm, setShowWaitlistForm] = useState(false);
    const [name, setName] = useState('');
    const [occupation, setOccupation] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    // Refs for cleanup
    const cursorTimeoutRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const deletingTimeoutRef = useRef(null);
    const videoRef = useRef(null);

    const typingTexts = [
        "How can I make this slide better?",
        "Update the table to reflect Anthropic's GTM",
        "Rewrite this section to be more concise",
        "Add a chart showing Q3 revenue growth",
        "Make the font size consistent across slides",
        "Change the color scheme to match our brand"
    ];

    // Check for mobile on mount
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        // Only show cursor animation on desktop
        if (!isMobile && buttonRef.current && !hasMoved) {
            const btn = buttonRef.current;
            const btnRect = btn.getBoundingClientRect();
            const scrollY = window.scrollY || window.pageYOffset;
            const scrollX = window.scrollX || window.pageXOffset;

            // Offset to place cursor at the left edge of the button, vertically centered
            const targetTop = btnRect.top + scrollY + btnRect.height / 2 + 16; // 16 = half cursor size
            const targetLeft = btnRect.left + scrollX + 120; // 40px to the left of the button

            // Animate after a short delay
            cursorTimeoutRef.current = setTimeout(() => {
                setCursorPos({ top: targetTop, left: targetLeft });
                setHasMoved(true);
            }, 2000); // Delay before moving
        }

        // Cleanup timeout on unmount
        return () => {
            if (cursorTimeoutRef.current) {
                clearTimeout(cursorTimeoutRef.current);
            }
        };
    }, [hasMoved, isMobile]);

    // Typing animation effect
    useEffect(() => {
        const handleTyping = () => {
            const fullText = typingTexts[currentTextIndex];
            
            if (isDeleting) {
                setCurrentText(fullText.substring(0, currentText.length - 1));
                setTypingSpeed(25);
            } else {
                setCurrentText(fullText.substring(0, currentText.length + 1));
                setTypingSpeed(50);
            }

            if (!isDeleting && currentText === fullText) {
                deletingTimeoutRef.current = setTimeout(() => setIsDeleting(true), 1500);
            } else if (isDeleting && currentText === '') {
                setIsDeleting(false);
                setCurrentTextIndex((prev) => (prev + 1) % typingTexts.length);
            }
        };

        typingTimeoutRef.current = setTimeout(handleTyping, typingSpeed);
        
        // Cleanup timeout on unmount
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            if (deletingTimeoutRef.current) {
                clearTimeout(deletingTimeoutRef.current);
            }
        };
    }, [currentText, isDeleting, currentTextIndex, typingSpeed, typingTexts]);

    // Load Calendly widget script with improved cleanup
    useEffect(() => {
        // Check if script already exists to prevent multiple loads
        const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
        
        if (!existingScript) {
            const script = document.createElement('script');
            script.src = 'https://assets.calendly.com/assets/external/widget.js';
            script.async = true;
            script.id = 'calendly-widget-script';
            document.body.appendChild(script);
            
            return () => {
                // More robust cleanup
                const scriptToRemove = document.getElementById('calendly-widget-script');
                if (scriptToRemove && document.body.contains(scriptToRemove)) {
                    document.body.removeChild(scriptToRemove);
                }
                
                // Also clean up any Calendly-related elements
                const calendlyWidgets = document.querySelectorAll('.calendly-inline-widget iframe');
                calendlyWidgets.forEach(widget => {
                    if (widget.parentNode) {
                        widget.parentNode.removeChild(widget);
                    }
                });
            };
        }
    }, []);

    const handleWaitlistSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            if (!name.trim() || !email || !email.includes('@')) {
                setSubmitMessage('Please fill in all required fields with valid information');
                setIsSubmitting(false);
                return;
            }

            // Send to Formspree
            const response = await fetch('https://formspree.io/f/mjkryqap', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: name,
                    occupation: occupation,
                    email: email,
            
                })
            });

            if (response.ok) {
                setSubmitMessage('You\'ve joined 60+ others on the waitlist!');
                setName('');
                setOccupation('');
                setEmail('');
            } else {
                throw new Error('Form submission failed');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitMessage('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Component cleanup on unmount
    useEffect(() => {
        return () => {
            // Clean up all timeouts
            if (cursorTimeoutRef.current) {
                clearTimeout(cursorTimeoutRef.current);
            }
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            if (deletingTimeoutRef.current) {
                clearTimeout(deletingTimeoutRef.current);
            }
            
            // Clean up video resources
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.src = '';
                videoRef.current.load();
            }
        };
    }, []);

    return (
        <div style={{
            minHeight: '100vh',
            width: '100vw',
            display: 'flex',
            flexDirection: 'column',
            background: 'none',
            position: 'relative',
            overflowX: 'hidden' // Prevent horizontal scrolling
        }}>
            {/* Moving Cursor - Only on desktop */}
            {!isMobile && (
                <div
                    style={{
                        position: 'absolute',
                        top: cursorPos.top,
                        left: cursorPos.left,
                        width: 25,
                        height: 25,
                        background: "url('/images/cursorimage2.png') no-repeat center center",
                        backgroundSize: 'contain',
                        zIndex: 1000,
                        pointerEvents: 'none',
                        transition: 'top 2.6s cubic-bezier(.77,0,.18,1), left 2.6s cubic-bezier(.77,0,.18,1)'
                    }}
                />
            )}

            {/* Top Bar */}
            <div style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: isMobile ? '24px 24px 0 24px' : '32px 48px 0 48px',
                boxSizing: 'border-box'
            }}>
                {/* Logo */}
                <div style={{
                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: isMobile ? '1.5rem' : '2rem',
                    color: '#222',
                    letterSpacing: '-0.02em'
                }}>
                    Carrie.ai
                </div>
                {/* Nav */}
                <div style={{
                    display: 'flex',
                    gap: isMobile ? 16 : 32,
                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                    fontWeight: 500,
                    fontSize: isMobile ? '0.9rem' : '1.05rem'
                }}>
                    <a href="/" style={{ color: '#222', textDecoration: 'none', opacity: 0.7 }}>Home</a>
                </div>
            </div>

            {/* Main Content */}
            <main style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                maxWidth: '100%',
                marginTop: isMobile ? 32 : 64,
                paddingBottom: isMobile ? 40 : 80,
                padding: isMobile ? '0 24px' : '0 48px',
                boxSizing: 'border-box'
            }}>
                {/* Headline - RESTORED ORIGINAL */}
                <h1 style={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 700,
                    fontSize: isMobile ? '2.5rem' : '4.5rem',
                    color: '#181c2a',
                    textAlign: 'center',
                    margin: '0 0 0 0',
                    letterSpacing: '-0.02em',
                    lineHeight: isMobile ? 1.2 : 1.1,
                    maxWidth: '100%',
                    wordBreak: 'break-word'
                }}>
                    Your AI PowerPoint Agent
                </h1>
                {/* Typing Animation Subheadline */}
                <div style={{
                    marginTop: isMobile ? 24 : 32,
                    marginBottom: isMobile ? 40 : 64,
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 400,
                    fontSize: isMobile ? '1rem' : '1.25rem',
                    color: '#666',
                    textAlign: 'center',
                    minHeight: isMobile ? '1.2em' : '1.5em',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: isMobile ? '0 16px' : '0'
                }}>
                    <span style={{
                        color: '#888',
                        fontStyle: 'italic'
                    }}>
                        "{currentText}"
                    </span>
                    <span style={{
                        marginLeft: '2px',
                        animation: 'blink 1s infinite',
                        color: '#888',
                        fontSize: '1em',
                        lineHeight: '1em'
                    }}>
                        |
                    </span>
                </div>
                {/* Video */}
                <div style={{
                    width: '100%',
                    maxWidth: isMobile ? '100%' : 900,
                    borderRadius: isMobile ? 12 : 18,
                    overflow: 'hidden',
                    boxShadow: '0 4px 32px rgba(0,0,0,0.07)',
                    background: '#fff',
                    margin: isMobile ? '0 auto 40px auto' : '0 auto 80px auto'
                }}>
                    <video
                        ref={videoRef}
                        src="/DemoCarrieNew.mp4"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        style={{
                            width: '100%',
                            height: isMobile ? '240px' : '480px',
                            display: 'block',
                            background: '#000',
                            objectFit: 'cover'
                        }}
                        poster="/video-poster.png"
                        onLoadStart={(e) => {
                            // Reduce quality for better performance
                            e.target.setAttribute('webkit-playsinline', 'true');
                        }}
                    >
                        Your browser does not support the video tag.
                    </video>
                </div>
                {/* Used By Section */}
                <div style={{
                    marginTop: 0,
                    marginBottom: isMobile ? 40 : 80,
                    width: '100%',
                    maxWidth: isMobile ? '100%' : 900,
                    textAlign: 'center'
                }}>
                    <div style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 400,
                        fontSize: isMobile ? '0.8rem' : '1rem',
                        color: '#888',
                        marginBottom: isMobile ? 32 : 48,
                        letterSpacing: '0.5px'
                    }}>
                        TRUSTED BY POWER USERS AT
                    </div>
                    
                    {/* Moving Icons Container */}
                    <div style={{
                        width: '100%',
                        overflow: 'hidden',
                        position: 'relative',
                        height: isMobile ? 120 : 200,
                        maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
                        WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)'
                    }}>
                        <div 
                            className="moving-logos"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: isMobile ? 40 : 80,
                                animation: 'scroll 25s linear infinite',
                                whiteSpace: 'nowrap',
                                width: 'fit-content'
                            }}
                        >
                            {/* First set of logos */}
                            <img src="/images/stanford.png" alt="Stanford" className="logo-img" style={{ height: isMobile ? 60 : 90 }} />
                            <img src="/images/utaustin.png" alt="UT Austin" className="logo-img" style={{ height: isMobile ? 30 : 45 }} />
                            <img src="/images/bcg.png" alt="BCG" className="logo-img" style={{ height: isMobile ? 30 : 45 }} />
                            <img src="/images/columbia.jpeg" alt="Columbia" className="logo-img" style={{ height: isMobile ? 60 : 90 }} />
                            
                            {/* Duplicate set for seamless loop */}
                            <img src="/images/stanford.png" alt="Stanford" className="logo-img" style={{ height: isMobile ? 60 : 90 }} />
                            <img src="/images/utaustin.png" alt="UT Austin" className="logo-img" style={{ height: isMobile ? 30 : 45 }} />
                            <img src="/images/bcg.png" alt="BCG" className="logo-img" style={{ height: isMobile ? 30 : 45 }} />
                            <img src="/images/columbia.jpeg" alt="Columbia" className="logo-img" style={{ height: isMobile ? 60 : 90 }} />
                            
                            {/* Third set for extra smoothness */}
                            <img src="/images/stanford.png" alt="Stanford" className="logo-img" style={{ height: isMobile ? 60 : 90 }} />
                            <img src="/images/utaustin.png" alt="UT Austin" className="logo-img" style={{ height: isMobile ? 30 : 45 }} />
                            <img src="/images/bcg.jpeg" alt="BCG" className="logo-img" style={{ height: isMobile ? 30 : 45 }} />
                            <img src="/images/columbia.jpeg" alt="Columbia" className="logo-img" style={{ height: isMobile ? 60 : 90 }} />
                        </div>
                    </div>
                </div>
                
                {/* Description */}
                <div style={{
                    marginTop: 0,
                    marginBottom: isMobile ? 24 : 32,
                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                    fontWeight: 400,
                    fontSize: isMobile ? '1rem' : '1.1rem',
                    color: '#444',
                    textAlign: 'center',
                    maxWidth: isMobile ? '100%' : 600,
                    lineHeight: 1.4,
                    padding: isMobile ? '0 16px' : '0'
                }}>
                    Ready to transform your PowerPoint workflow with AI?
                </div>

                {/* Two Options Explanation */}
                <div style={{
                    marginBottom: isMobile ? 32 : 40,
                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                    fontWeight: 400,
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    color: '#666',
                    textAlign: 'center',
                    maxWidth: isMobile ? '100%' : 600,
                    lineHeight: 1.5,
                    padding: isMobile ? '0 16px' : '0'
                }}>
                    <strong style={{ color: '#7f6fff' }}>Book a Demo</strong> for immediate access and personalized walkthrough<br />
                    <em>or</em><br />
                    <strong style={{ color: '#7f6fff' }}>Join Beta Waitlist</strong> for early access to the full version
                </div>

                {/* CTA Section */}
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 16 : 24,
                    alignItems: 'center',
                    marginBottom: isMobile ? 40 : 60
                }}>
               

                    {/* Waitlist Button/Form */}
                    {!showWaitlistForm ? (
                        <button
                            ref={buttonRef}
                            onClick={() => setShowWaitlistForm(true)}
                            style={{
                                color: '#fff',
                                background: 'linear-gradient(90deg, #7f6fff 0%, #a084ee 100%)',
                                border: 'none',
                                borderRadius: isMobile ? 16 : 20,
                                padding: isMobile ? '0.7rem 2rem' : '0.85rem 2.5rem',
                                fontWeight: 600,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                textDecoration: 'none',
                                boxShadow: '0 2px 12px rgba(160,132,238,0.10)',
                                transition: 'all 0.2s',
                                display: 'inline-block',
                                textAlign: 'center',
                                cursor: 'pointer',
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif"
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = 'linear-gradient(90deg, #6b5ce6 0%, #8c7ae0 100%)';
                                e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'linear-gradient(90deg, #7f6fff 0%, #a084ee 100%)';
                                e.target.style.transform = 'translateY(0)';
                            }}
                        >
                            Join Beta Waitlist
                        </button>
                    ) : submitMessage && submitMessage.includes('joined') ? (
                        // Success state
                        <div style={{
                            background: 'rgba(40, 167, 69, 0.1)',
                            borderRadius: isMobile ? 16 : 20,
                            padding: isMobile ? '16px 24px' : '20px 32px',
                            border: '2px solid #28a745',
                            animation: 'fadeIn 0.3s ease-in-out',
                            textAlign: 'center'
                        }}>
                            <p style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 600,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                color: '#28a745',
                                margin: 0
                            }}>
                                You've joined 60+ others on the waitlist! 🎉
                            </p>
                        </div>
                    ) : (
                        // Form state
                        <div style={{
                            background: 'rgba(245,245,250,0.9)',
                            borderRadius: isMobile ? 16 : 20,
                            padding: isMobile ? '12px' : '16px',
                            border: '2px solid #7f6fff',
                            minWidth: isMobile ? '280px' : '320px',
                            animation: 'fadeIn 0.3s ease-in-out'
                        }}>
                            <form onSubmit={handleWaitlistSubmit} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12
                            }}>
                                <input
                                    type="text"
                                    name="name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Name"
                                    required
                                    autoFocus
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '10px 12px' : '12px 16px',
                                        borderRadius: 8,
                                        border: '1px solid #ddd',
                                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                        outline: 'none',
                                        transition: 'border-color 0.2s',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#7f6fff';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#ddd';
                                    }}
                                />
                                <input
                                    type="text"
                                    name="occupation"
                                    value={occupation}
                                    onChange={(e) => setOccupation(e.target.value)}
                                    placeholder="Occupation (optional)"
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '10px 12px' : '12px 16px',
                                        borderRadius: 8,
                                        border: '1px solid #ddd',
                                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                        outline: 'none',
                                        transition: 'border-color 0.2s',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#7f6fff';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#ddd';
                                    }}
                                />
                                <input
                                    type="email"
                                    name="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '10px 12px' : '12px 16px',
                                        borderRadius: 8,
                                        border: '1px solid #ddd',
                                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                        outline: 'none',
                                        transition: 'border-color 0.2s',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#7f6fff';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#ddd';
                                    }}
                                />
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '12px 16px' : '14px 20px',
                                        background: isSubmitting ? '#ccc' : 'linear-gradient(90deg, #7f6fff 0%, #a084ee 100%)',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 8,
                                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                                        fontWeight: 600,
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        transition: 'background 0.2s',
                                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    {isSubmitting ? 'Joining...' : 'Join'}
                                </button>
                            </form>

                            {submitMessage && !submitMessage.includes('joined') && (
                                <p style={{
                                    marginTop: 8,
                                    marginBottom: 0,
                                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                                    color: '#dc3545',
                                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                    textAlign: 'center'
                                }}>
                                    {submitMessage}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </main>

            {/* Waitlist Form Section with Gray Background */}
            <section id="waitlist-form" style={{
                width: '100%',
                background: 'rgba(245,245,250,0.7)',
                padding: isMobile ? '40px 0 40px 0' : '60px 0 60px 0',
                overflowX: 'hidden'
            }}>
            
            </section>

            {/* Calendly Widget Section with Gray Background */}
            <section style={{
                width: '100%',
                background: 'rgba(245,245,250,0.7)',
                padding: isMobile ? '20px 0 40px 0' : '30px 0 60px 0',
                overflowX: 'hidden'
            }}>
                <div style={{
                    maxWidth: isMobile ? '100%' : 900,
                    margin: '0 auto',
                    padding: isMobile ? '0 24px' : '0 48px',
                    textAlign: 'center'
                }}>
                    <h2 style={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 700,
                        fontSize: isMobile ? '1.3rem' : '1.6rem',
                        color: '#181c2a',
                        margin: '0 0 60px 0',
                        letterSpacing: '-0.02em'
                    }}>
                        Schedule Your Demo
                    </h2>

                    {/* Calendly Inline Widget */}
                    <div
                        className="calendly-inline-widget"
                        data-url="https://calendly.com/carrieai/carrieai"
                        style={{
                            minWidth: '320px',
                            width: '100%',
                            maxWidth: '100%',
                            height: isMobile ? '500px' : '700px',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                        }}
                    ></div>
                </div>
            </section>

            {/* Beta Features Section - RESTORED */}
            <section style={{
                width: '100%',
                maxWidth: '100%',
                background: 'linear-gradient(135deg, rgba(127, 111, 255, 0.03), rgba(160, 132, 238, 0.03))',
                border: '1px solid rgba(127, 111, 255, 0.08)',
                borderLeft: 'none',
                borderRight: 'none',
                padding: isMobile ? '40px 0 40px 0' : '80px 0 80px 0',
                overflowX: 'hidden'
            }}>
                <div style={{
                    maxWidth: isMobile ? '100%' : 900,
                    margin: '0 auto',
                    padding: isMobile ? '0 24px' : '0 48px',
                    textAlign: 'center'
                }}>
                    <h2 style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 700,
                        fontSize: isMobile ? '1.8rem' : '2.2rem',
                        color: '#181c2a',
                        margin: '0 0 24px 0',
                        letterSpacing: '-0.01em'
                    }}>
                        What's Available in Beta
                    </h2>
                    
                    <p style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 400,
                        fontSize: isMobile ? '1rem' : '1.15rem',
                        color: '#666',
                        margin: isMobile ? '0 0 40px 0' : '0 0 56px 0',
                        maxWidth: isMobile ? '100%' : 600,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        lineHeight: 1.5
                    }}>
                    </p>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: isMobile ? 24 : 32,
                        marginTop: 0
                    }}>
                        {/* Feature 1 */}
                        <div style={{
                            background: 'white',
                            borderRadius: isMobile ? 12 : 16,
                            padding: isMobile ? '24px 20px' : '32px 24px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                            border: '1px solid rgba(0,0,0,0.06)',
                            textAlign: 'left',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}>
                            <div style={{
                                fontSize: isMobile ? '1.5rem' : '2rem',
                                marginBottom: isMobile ? 12 : 16,
                                textAlign: 'center'
                            }}>🗣️</div>
                            <h3 style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 600,
                                fontSize: isMobile ? '1.1rem' : '1.2rem',
                                color: '#181c2a',
                                margin: '0 0 12px 0'
                            }}>
                                Conversational Editing
                            </h3>
                            <p style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 400,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                color: '#555',
                                margin: 0,
                                lineHeight: 1.5
                            }}>
                                Talk naturally with your slides. Ask to rewrite text, adjust formatting, or explain complex concepts in simpler terms.
                            </p>
                        </div>

                        {/* Feature 2 */}
                        <div style={{
                            background: 'white',
                            borderRadius: isMobile ? 12 : 16,
                            padding: isMobile ? '24px 20px' : '32px 24px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                            border: '1px solid rgba(0,0,0,0.06)',
                            textAlign: 'left',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}>
                            <div style={{
                                fontSize: isMobile ? '1.5rem' : '2rem',
                                marginBottom: isMobile ? 12 : 16,
                                textAlign: 'center'
                            }}>📊</div>
                            <h3 style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 600,
                                fontSize: isMobile ? '1.1rem' : '1.2rem',
                                color: '#181c2a',
                                margin: '0 0 12px 0'
                            }}>
                                Smart Data Updates
                            </h3>
                            <p style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 400,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                color: '#555',
                                margin: 0,
                                lineHeight: 1.5
                            }}>
                                Replace table data, update charts, and refresh slide content with new information seamlessly and accurately.
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div style={{
                            background: 'white',
                            borderRadius: isMobile ? 12 : 16,
                            padding: isMobile ? '24px 20px' : '32px 24px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                            border: '1px solid rgba(0,0,0,0.06)',
                            textAlign: 'left',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}>
                            <div style={{
                                fontSize: isMobile ? '1.5rem' : '2rem',
                                marginBottom: isMobile ? 12 : 16,
                                textAlign: 'center'
                            }}>🎯</div>
                            <h3 style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 600,
                                fontSize: isMobile ? '1.1rem' : '1.2rem',
                                color: '#181c2a',
                                margin: '0 0 12px 0'
                            }}>
                                Intelligent Layouts
                            </h3>
                            <p style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 400,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                color: '#555',
                                margin: 0,
                                lineHeight: 1.5
                            }}>
                                Move icons, adjust spacing, and reorganize elements with precision. Perfect alignment and spacing, every time.
                            </p>
                        </div>

                        {/* Feature 4 */}
                        <div style={{
                            background: 'white',
                            borderRadius: isMobile ? 12 : 16,
                            padding: isMobile ? '24px 20px' : '32px 24px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                            border: '1px solid rgba(0,0,0,0.06)',
                            textAlign: 'left',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}>
                            <div style={{
                                fontSize: isMobile ? '1.5rem' : '2rem',
                                marginBottom: isMobile ? 12 : 16,
                                textAlign: 'center'
                            }}>✨</div>
                            <h3 style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 600,
                                fontSize: isMobile ? '1.1rem' : '1.2rem',
                                color: '#181c2a',
                                margin: '0 0 12px 0'
                            }}>
                                Manager Feedback Integration
                            </h3>
                            <p style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 400,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                color: '#555',
                                margin: 0,
                                lineHeight: 1.5
                            }}>
                                Apply feedback instantly. Transform suggestions into polished edits with context-aware improvements.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feedback Section - RESTORED */}
            <section style={{
                width: '100%',
                padding: isMobile ? '40px 0 40px 0' : '60px 0 60px 0'
            }}>
                <div style={{
                    maxWidth: isMobile ? '100%' : 600,
                    margin: '0 auto',
                    padding: isMobile ? '0 24px' : '0 48px',
                    textAlign: 'center'
                }}>
                    <h2 style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 700,
                        fontSize: isMobile ? '1.5rem' : '1.8rem',
                        color: '#181c2a',
                        margin: '0 0 16px 0',
                        letterSpacing: '-0.01em'
                    }}>
                        How can we make this better?
                    </h2>
                    
                    <p style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 400,
                        fontSize: isMobile ? '0.95rem' : '1.05rem',
                        color: '#666',
                        margin: '0 0 32px 0',
                        lineHeight: 1.5
                    }}>
                        Your feedback shapes the future of Carrie.ai. Reach out with suggestions, bugs, or just to say hello.
                    </p>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: isMobile ? 12 : 24,
                        flexWrap: 'wrap',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center'
                    }}>
                        {/* Email */}
                        <a 
                            href="mailto:wafi@utexas.edu" 
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: isMobile ? '12px 16px' : '12px 20px',
                                background: '#f8f9fa',
                                borderRadius: 12,
                                textDecoration: 'none',
                                color: '#444',
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 500,
                                fontSize: isMobile ? '0.85rem' : '0.95rem',
                                transition: 'all 0.2s ease',
                                border: '1px solid #e9ecef',
                                width: isMobile ? '100%' : 'auto',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                                maxWidth: isMobile ? '300px' : 'none'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#7f6fff';
                                e.target.style.color = '#fff';
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(127, 111, 255, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = '#f8f9fa';
                                e.target.style.color = '#444';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                            }}
                        >
                            <span style={{ fontSize: '1.1em' }}>📧</span>
                            wafi@utexas.edu
                        </a>

                        {/* X (Twitter) */}
                        <a 
                            href="https://twitter.com/wafitoffy_" 
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: isMobile ? '12px 16px' : '12px 20px',
                                background: '#f8f9fa',
                                borderRadius: 12,
                                textDecoration: 'none',
                                color: '#444',
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 500,
                                fontSize: isMobile ? '0.85rem' : '0.95rem',
                                transition: 'all 0.2s ease',
                                border: '1px solid #e9ecef',
                                width: isMobile ? '100%' : 'auto',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                                maxWidth: isMobile ? '300px' : 'none'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#000';
                                e.target.style.color = '#fff';
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = '#f8f9fa';
                                e.target.style.color = '#444';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                            }}
                        >
                            <svg 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="currentColor"
                                style={{ flexShrink: 0 }}
                            >
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                            @wafitoffy_
                        </a>

                        {/* Phone */}
                        <a 
                            href="tel:+14695446042" 
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: isMobile ? '12px 16px' : '12px 20px',
                                background: '#f8f9fa',
                                borderRadius: 12,
                                textDecoration: 'none',
                                color: '#444',
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 500,
                                fontSize: isMobile ? '0.85rem' : '0.95rem',
                                transition: 'all 0.2s ease',
                                border: '1px solid #e9ecef',
                                width: isMobile ? '100%' : 'auto',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                                maxWidth: isMobile ? '300px' : 'none'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#34c759';
                                e.target.style.color = '#fff';
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(52, 199, 89, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = '#f8f9fa';
                                e.target.style.color = '#444';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                            }}
                        >
                            <span style={{ fontSize: '1.1em' }}>📱</span>
                            (469) 544-6042
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer style={{
                width: '100%',
                padding: isMobile ? '32px 0 24px 0' : '48px 0 32px 0',
                textAlign: 'center',
                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                fontWeight: 400,
                fontSize: isMobile ? '0.85rem' : '0.95rem',
                color: '#888',
                marginTop: 0
            }}>
            </footer>
        </div>
    );
}

export default InstallPage;